import { observer } from 'mobx-react';

import { CircularProgress, IconButton, Link } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';

import Box from '@mui/material/Box';

import { Button } from 'components/Button';

import { ButtonWrapper, Container, SubmissionContainer } from 'containers/EntitiesFormSubmission/styles';

import { useStore } from 'utils/hooks/store';

import { getEntitySubmissionConfig, isAuditsSubmission } from 'containers/EntitiesFormSubmission/config';

import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EnumOptions, FormBuilderProperties, ImageType } from 'utils/api/modules/entities/entities.types';

import CustomFieldDisplayValue from 'components/SharedFormComponents/CustomFieldDisplayValue/CustomFieldDisplayValue';
import LookupRecordsAnswer from 'components/Audits/LookupRecordsAnswer';
import { LookupAnswerEntry } from 'components/Audits/lookupRecords';

import { RepeatingProperties } from 'containers/EntitiesFormSubmission/components/RepeatingGroupField';

import { EntityModules, ProtectorType } from 'core/constants';

import { colors } from 'theme/colors';

import {
  PreviewSection,
  PreviewSectionTitle,
  PreviewSectionDescription,
  SubHeading,
  Title,
  PreviewSectionHeader,
  PreviewSectionContent,
  PreviewSectionRow,
} from './styles';
import FormDraftAdditionalComments from '../FormDraftAdditionalComments';
import SubmittedModal from '../SubmittedModal';
import React from 'react';

const PreviewSectionItem: React.FC<{
  section: FormBuilderProperties;
  /** Audits only — Events and public reporting keep choice answers as plain text. */
  choiceChips: boolean;
  getContentForKey: (questionKey: string) => AnswerTypes | undefined;
  editSection: () => void;
}> = ({ section, choiceChips, getContentForKey, editSection }): React.ReactElement => (
  <PreviewSection>
    <PreviewSectionHeader>
      <div>
        <PreviewSectionTitle>{section.title}</PreviewSectionTitle>
        {section.description && <PreviewSectionDescription>{section.description}</PreviewSectionDescription>}
      </div>
      <IconButton onClick={editSection} data-testid="edit-button">
        <EditIcon name="edit" htmlColor={colors.primary.main} />
      </IconButton>
    </PreviewSectionHeader>
    <PreviewSectionContent>
      {section.order.map((item) => {
        const question = section.properties[item];
        const questionType = question.questionType as ProtectorType;
        const value = getContentForKey(item) as AnswerTypes;
        const lookupType =
          questionType === ProtectorType.Lookup
            ? (question.properties.lookupType as unknown as EntityModules)
            : undefined;

        return (
          <React.Fragment key={item}>
            <PreviewSectionRow
              style={questionType === ProtectorType.Signature ? { alignItems: 'flex-start' } : undefined}
            >
              <h1>{question.description}</h1>
              <Box sx={{ flex: 6, display: 'flex', alignItems: 'center' }} data-testid={`preview-answer-${item}`}>
                {/* A multi-record answer reads back as the chips the auditor just picked, not a column of links. */}
                {choiceChips && questionType === ProtectorType.Lookup && Array.isArray(value) ? (
                  <LookupRecordsAnswer records={value as unknown as LookupAnswerEntry[]} module={lookupType} />
                ) : (
                  <CustomFieldDisplayValue
                    schema={undefined}
                    type={questionType}
                    value={value}
                    lookupType={lookupType}
                    repeatingGroup={
                      (question.properties as unknown as { repeatingGroup?: RepeatingProperties }).repeatingGroup
                    }
                    choiceChips={choiceChips}
                    choiceOptions={choiceChips ? (question.properties.enum as unknown as EnumOptions) : undefined}
                  />
                )}
              </Box>
            </PreviewSectionRow>

            {question.properties.image && (
              <img
                style={{ width: '100%', height: '100%', objectFit: 'cover', marginTop: '16px' }}
                src={(question.properties.image as unknown as ImageType).file as string}
                alt="question"
              />
            )}
            <hr style={{ width: '100%', borderColor: 'rgba(0, 0, 0, 0.18)', borderTop: 'none' }} />
          </React.Fragment>
        );
      })}
    </PreviewSectionContent>
  </PreviewSection>
);

const FormPreview: React.FC = (): React.ReactElement => {
  const {
    entitiesFormSubmission: {
      onSubmit,
      submitting,
      editSection,
      editSectionOffline,
      previewData,
      getContentForKey,
      showSubmittedModal,
      isPublicSubmission,
      entityType,
    },
  } = useStore();

  // audits-only attribute: this step also serves Events drafts and public event reporting
  const isAudits = isAuditsSubmission(entityType);

  return (
    <SubmissionContainer>
      <Container>
        <Title>Review and Submit</Title>
        <SubHeading>
          Please review your answers below to ensure all information is accurate.
          {!isAudits &&
            ' If you have any additional comments, files, or images to include, you can add them in the section provided at the bottom of this page.'}
        </SubHeading>
        {previewData?.stack.map((item) => (
          <PreviewSectionItem
            key={item}
            section={previewData.form.form.properties[item]}
            choiceChips={isAudits}
            // Audits keep the stored option keys: the chip resolves label and colour from the options itself.
            getContentForKey={(questionKey) =>
              isAudits ? getContentForKey(questionKey) : getContentForKey(questionKey, item)
            }
            editSection={() => (isPublicSubmission ? editSectionOffline(item) : editSection(item))}
          />
        ))}
        {isPublicSubmission && (
          <SubHeading>
            By submitting this form, you acknowledge that your data will be processed in accordance with our{' '}
            <Link underline="none" href="https://www.vatix.com/privacy" target="_blank">
              Privacy Policy
            </Link>
            .
          </SubHeading>
        )}
      </Container>
      <ButtonWrapper>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          onClick={() =>
            isPublicSubmission
              ? editSectionOffline(previewData?.stack.slice(-1)[0] ?? '')
              : editSection(previewData?.stack.slice(-1)[0] ?? '')
          }
          disabled={submitting}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          data-testid={isAudits ? 'audit-fillout-submit' : undefined}
          onClick={onSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress color="inherit" size="16px" /> : undefined}
        >
          Submit
        </Button>
      </ButtonWrapper>
      {/* Audits have no record-level feed: a comment here saves with a null question, unreachable afterwards. */}
      {!isPublicSubmission && !isAudits && <FormDraftAdditionalComments />}
      <SubmittedModal openModal={showSubmittedModal} entityLabel={getEntitySubmissionConfig(entityType).recordLabel} />
    </SubmissionContainer>
  );
};

export default observer(FormPreview);
